import React from "react";
import { useParams } from "react-router-dom";
import PageWrapper from "../../../../../components/PageWrapper/PageWrapper";
import { LegacyLoader } from "../../../../../components/Loader/Loader";
import StudentCollectionDetails from "../../../../../components/StudentCollectionDetails/StudentCollectionDetails";
import ClassroomName from "../ClassroomName/ClassroomName";
import TabTitle from "../../../../../components/TabTitle/TabTitle";
import { defineMessages, useIntl } from "react-intl";

import { Chip } from "@evidenceb/athena-common/design-system/Chip";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import { useClassroomQuery } from "../../api/queries";
import { Classroom } from "../../../../../interfaces/User";
import ModifyStudentsSettings from "../../../../../components/StudentCollectionDetails/ModifyStudentsSettings/ModifyStudentsSettings";
import DownloadStudentsSettings from "../../../../../components/StudentCollectionDetails/DownloadStudentsSettings/DownloadStudentsSettings";
import { CodePrintingProvider } from "../../../../../components/UsersCodePrinting/UsersCodePrintingContext";
import UsersCodePrintingSelection from "../../../../../components/UsersCodePrinting/UserSelection/UsersSelection";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";
import useTheme from "../../../../../hooks/useTheme";

import "./ClassroomDetails.scss";

const ClassroomDetails = () => {
    const { classroomId } = useParams<{ classroomId: string }>();
    const isOnline = useOnlineStatus();
    const classManagementFlag = useFeatureFlag("classManagement");
    const studentManagementFlag = useFeatureFlag("studentManagement");
    const isSpecimen = useFeatureFlag("isSpecimen");

    const { data: classroom } = useClassroomQuery(classroomId, "classroom");

    if (!classroom) return <LegacyLoader />;

    const students = classroom.students.map(({ id }) => id);

    return (
        <PageWrapper>
            <TabTitle title={classroom.name} />

            <CodePrintingProvider key={students.join()} students={students}>
                <StudentCollectionDetails
                    type="classroom"
                    className="classroom-details"
                    collection={classroom}
                    title={
                        classManagementFlag ? (
                            <ExtendedClassroomTitle {...classroom} />
                        ) : (
                            <h1>{classroom.name}</h1>
                        )
                    }
                    studentsListOptions={
                        studentManagementFlag ? (
                            <div className="classroom-details__settings">
                                {isOnline && !isSpecimen && (
                                    <ModifyStudentsSettings
                                        classroom={classroom}
                                    />
                                )}

                                <DownloadStudentsSettings
                                    collectionId={classroomId}
                                />
                            </div>
                        ) : undefined
                    }
                    studentsListFooter={
                        studentManagementFlag ? (
                            <UsersCodePrintingSelection.Confirmation />
                        ) : undefined
                    }
                />
            </CodePrintingProvider>
        </PageWrapper>
    );
};

export default ClassroomDetails;

const ExtendedClassroomTitle = (
    classroom: Pick<Classroom, "id" | "name" | "code">
) => {
    const intl = useIntl();
    const theme = useTheme();

    return (
        <>
            <h1>
                <ClassroomName id={classroom.id} name={classroom.name} />
            </h1>
            <span className="classroom-details__code">
                <span>{intl.formatMessage(messages.classroomCode)}</span>
                <Chip input={classroom.code} colorMode={theme} />
            </span>
        </>
    );
};

const messages = defineMessages({
    classroomCode: {
        id: "user-list-classroomCode",
        defaultMessage: "Classroom code",
    },
});
